import { NavLink, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import removeData from '../../helpers/removeData';
import { useLazyLogoutQuery } from '../../app/services/auth';

import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  Divider,
  IconButton,
  Typography,
  Collapse,
  ListItemIcon,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandLess,
  ExpandMore,
  People as PeopleIcon,
  Build as BuildIcon,
  LocationOn as LocationIcon,
  Inventory as InventoryIcon,
  HandymanOutlined as MaintenanceTypeIcon,
  CalendarMonth as CalendarIcon,
  History as HistoryIcon,
  ExitToApp as LogoutIcon,
  AccountCircle as AccountIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import { styled } from '@mui/material';

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: 280,
    backgroundColor: '#fff',
    paddingTop: theme.spacing(2),
  },
}));

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0, 2, 2, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledNavLink = styled(NavLink)(({ theme }) => ({
  textDecoration: 'none',
  color: 'inherit',
  display: 'block',
  width: '100%',
  '&.active': {
    '& .MuiListItemButton-root': {
      backgroundColor: theme.palette.primary.light + '20',
      borderLeft: `3px solid ${theme.palette.primary.main}`,
    },
    '& .MuiListItemText-primary': {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
    },
  },
}));

function MobileDrawer({ open, onClose, user }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [logout] = useLazyLogoutQuery();
  const [machinesOpen, setMachinesOpen] = useState(false);

  const handleMachinesClick = () => {
    setMachinesOpen(!machinesOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      Cookies.remove('token');
      sessionStorage.clear();
      removeData(dispatch);
      navigate('/sign-in');
      onClose();
    } catch (error) {
      console.error(error);
    }
  };

  const handleNavClick = () => {
    onClose();
  };

  return (
    <StyledDrawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile.
      }}
    >
      <DrawerHeader>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Menu
        </Typography>
        <IconButton onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </DrawerHeader>

      <List sx={{ px: 1, py: 2 }}>
        {/* Admin Links */}
        {user?.role === 'admin' && (
          <>
            <StyledNavLink to="/users" onClick={handleNavClick}>
              <ListItem disablePadding>
                <ListItemButton>
                  <ListItemIcon>
                    <PeopleIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('generic.users')} />
                </ListItemButton>
              </ListItem>
            </StyledNavLink>

            <StyledNavLink to="/audit-logs" onClick={handleNavClick}>
              <ListItem disablePadding>
                <ListItemButton>
                  <ListItemIcon>
                    <HistoryIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('generic.auditLogs')} />
                </ListItemButton>
              </ListItem>
            </StyledNavLink>
            <Divider sx={{ my: 1 }} />
          </>
        )}

        {/* Machines Dropdown */}
        <ListItem disablePadding>
          <ListItemButton onClick={handleMachinesClick}>
            <ListItemIcon>
              <BuildIcon />
            </ListItemIcon>
            <ListItemText primary={t('generic.machines')} />
            {machinesOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>

        <Collapse in={machinesOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <StyledNavLink to="/machines" onClick={handleNavClick}>
              <ListItem sx={{ pl: 4 }}>
                <ListItemButton>
                  <ListItemText primary={t('generic.machinesList')} />
                </ListItemButton>
              </ListItem>
            </StyledNavLink>

            <StyledNavLink to="/machines-groups" onClick={handleNavClick}>
              <ListItem sx={{ pl: 4 }}>
                <ListItemButton>
                  <ListItemText primary={t('generic.machineGroups')} />
                </ListItemButton>
              </ListItem>
            </StyledNavLink>

            <StyledNavLink to="/locations" onClick={handleNavClick}>
              <ListItem sx={{ pl: 4 }}>
                <ListItemButton>
                  <ListItemIcon>
                    <LocationIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={t('generic.locations')} />
                </ListItemButton>
              </ListItem>
            </StyledNavLink>

            <StyledNavLink to="/supplies" onClick={handleNavClick}>
              <ListItem sx={{ pl: 4 }}>
                <ListItemButton>
                  <ListItemIcon>
                    <InventoryIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={t('generic.supplies')} />
                </ListItemButton>
              </ListItem>
            </StyledNavLink>

            <StyledNavLink to="/maintenance-types" onClick={handleNavClick}>
              <ListItem sx={{ pl: 4 }}>
                <ListItemButton>
                  <ListItemIcon>
                    <MaintenanceTypeIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={t('generic.maintenanceTypes')} />
                </ListItemButton>
              </ListItem>
            </StyledNavLink>
          </List>
        </Collapse>

        {/* Calendar */}
        <StyledNavLink to="/" onClick={handleNavClick}>
          <ListItem disablePadding>
            <ListItemButton>
              <ListItemIcon>
                <CalendarIcon />
              </ListItemIcon>
              <ListItemText primary={t('header.Calendar')} />
            </ListItemButton>
          </ListItem>
        </StyledNavLink>
      </List>

      <Box sx={{ flexGrow: 1 }} />

      {/* Bottom Section */}
      <Divider />
      <List sx={{ px: 1, py: 2 }}>
        <StyledNavLink to="/user-profile" onClick={handleNavClick}>
          <ListItem disablePadding>
            <ListItemButton>
              <ListItemIcon>
                <AccountIcon />
              </ListItemIcon>
              <ListItemText primary={t('generic.profile')} />
            </ListItemButton>
          </ListItem>
        </StyledNavLink>

        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary={t('header.Logout')} />
          </ListItemButton>
        </ListItem>
      </List>
    </StyledDrawer>
  );
}

export default MobileDrawer;
